import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    Box, Typography, Button, TextField, Paper, Chip,
    Card, CardContent, Divider, IconButton, useTheme, useMediaQuery,
    CircularProgress, ToggleButton, ToggleButtonGroup, Tooltip,
    FormControl, InputLabel, Select, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
    Snackbar, Alert,
} from '@mui/material';
import { Search, Add, Visibility } from '@mui/icons-material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { useNavigate } from 'react-router-dom';

import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';
import { NewTicketDialog } from './NewTicketDialog';

interface TicketRecord {
    _id: string;
    title: string;
    description: string;
    productName: string;
    priority: string;
    contactNumber: string;
    mobileNumber: string;
    emailAddress: string;
    ccEmailAddresses: string[];
    companyId: string;
    createdBy: string;
    ticketStatus: string;
    createdAt: string;
    updatedAt: string;
}

const PRIORITY_LABELS: Record<string, string> = {
    P1: 'P1 - Blocker',
    P2: 'P2 - Urgent',
    P3: 'P3 - Important',
    P4: 'P4 - Minor',
};

const PRIORITY_COLORS: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
    P1: 'error',
    P2: 'warning',
    P3: 'info',
    P4: 'default',
};

const SORT_OPTIONS = [
    { value: 'createdAt', label: 'Created At' },
    { value: 'updatedAt', label: 'Last Updated' },
];

const FILTER_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'ACTIVE', label: 'Open' },
    { value: 'CLOSED', label: 'Closed' },
    { value: 'P1', label: 'Priority P1' },
    { value: 'P2', label: 'Priority P2' },
    { value: 'P3', label: 'Priority P3' },
    { value: 'P4', label: 'Priority P4' },
];

const VIEWMODE_STORAGE_KEY = 'support-tickets-view-mode';
const TILE_PAGE_SIZE = 12;
const SEARCH_DEBOUNCE_MS = 400;

export const MyTickets: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    const [viewMode, setViewMode] = useState<'tile' | 'table'>(() => {
        const stored = localStorage.getItem(VIEWMODE_STORAGE_KEY);
        return stored === 'table' ? 'table' : 'tile';
    });

    const [tickets, setTickets] = useState<TicketRecord[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [filterBy, setFilterBy] = useState('ACTIVE');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

    const [tilePage, setTilePage] = useState(0);
    const [tablePage, setTablePage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery.trim());
        }, SEARCH_DEBOUNCE_MS);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [searchQuery]);

    const buildQueryParams = useCallback((page: number, limit: number): string => {
        const params = new URLSearchParams();
        if (debouncedSearch) params.set('search', debouncedSearch);
        params.set('sortBy', sortBy);
        params.set('sortOrder', sortOrder);
        params.set('page', String(page));
        params.set('limit', String(limit));
        if (filterBy === 'ACTIVE' || filterBy === 'CLOSED') {
            params.set('status', filterBy);
        } else if (filterBy) {
            params.set('priority', filterBy);
        }
        return params.toString();
    }, [debouncedSearch, sortBy, sortOrder, filterBy]);

    const fetchTickets = useCallback(async (page: number, limit: number, append: boolean = false) => {
        append ? setLoadingMore(true) : setLoading(true);
        try {
            const qs = buildQueryParams(page, limit);
            const response = await apiClient.get(`/support/tickets?${qs}`);
            const { tickets: newTickets, totalCount: newTotal } = response.data;
            setTickets(prev => append ? [...prev, ...newTickets] : (newTickets || []));
            setTotalCount(newTotal ?? 0);
        } catch (error) {
            Logger.error('MyTickets', `Failed to fetch tickets: ${error}`);
        } finally {
            append ? setLoadingMore(false) : setLoading(false);
        }
    }, [buildQueryParams]);

    useEffect(() => {
        setTilePage(0);
        setTablePage(0);
        setTickets([]);
        const limit = viewMode === 'tile' ? TILE_PAGE_SIZE : rowsPerPage;
        fetchTickets(0, limit);
    }, [debouncedSearch, filterBy, sortBy, sortOrder, viewMode, rowsPerPage]);  // eslint-disable-line react-hooks/exhaustive-deps

    const handleTileLoadMore = () => {
        const nextPage = tilePage + 1;
        setTilePage(nextPage);
        fetchTickets(nextPage, TILE_PAGE_SIZE, true);
    };

    const handleTablePageChange = (_e: unknown, newPage: number) => {
        setTablePage(newPage);
        fetchTickets(newPage, rowsPerPage);
    };

    const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newRpp = parseInt(e.target.value, 10);
        setRowsPerPage(newRpp);
        setTablePage(0);
    };

    const hasMoreTiles = tickets.length < totalCount;

    const handleCreate = () => setDialogOpen(true);

    const handleTicketCreated = () => {
        setDialogOpen(false);
        setSnackbar({ open: true, message: 'Ticket created successfully!', severity: 'success' });
        setTilePage(0);
        setTablePage(0);
        const limit = viewMode === 'tile' ? TILE_PAGE_SIZE : rowsPerPage;
        fetchTickets(0, limit);
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString(undefined, {
            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    };

    const renderTileView = () => (
        <Box display="flex" flexDirection="column" gap={2.5}>
            <Box display="flex" flexWrap="wrap" gap={2.5} sx={{ justifyContent: 'center' }}>
                {tickets.map((ticket) => {
                    if (!isMobile) {
                        return (
                            <Card key={ticket._id} elevation={0}
                                onClick={() => navigate(`/support/my-tickets/${ticket._id}`)}
                                sx={{
                                    width: 320, border: '1px solid', borderColor: 'divider', borderRadius: 2,
                                    display: 'flex', flexDirection: 'column', transition: 'box-shadow 0.2s',
                                    cursor: 'pointer',
                                    '&:hover': { boxShadow: theme.shadows[4] },
                                }}>
                                <CardContent sx={{ flexGrow: 1, pb: 1.5 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                        <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3, wordBreak: 'break-word', flex: 1, mr: 1 }}>
                                            {ticket.title}
                                        </Typography>
                                        <Chip label={ticket.ticketStatus} size="small"
                                            color={ticket.ticketStatus === 'ACTIVE' ? 'success' : 'secondary'}
                                            sx={{ fontWeight: 600, flexShrink: 0 }} />
                                    </Box>
                                    <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                                        <Chip label={PRIORITY_LABELS[ticket.priority] || ticket.priority}
                                            size="small" color={PRIORITY_COLORS[ticket.priority] || 'default'} />
                                        <Chip label={ticket.productName} size="small" variant="outlined" />
                                    </Box>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="body2" color="text.secondary" sx={{
                                        overflow: 'hidden', textOverflow: 'ellipsis',
                                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                                    }}>
                                        {ticket.description}
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
                                        Created: {formatDate(ticket.createdAt)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        );
                    }

                    return (
                        <Card key={ticket._id} elevation={0}
                            onClick={() => navigate(`/support/my-tickets/${ticket._id}`)}
                            sx={{
                                width: '100%', maxWidth: 400, borderRadius: 2, border: '1px solid',
                                borderColor: 'divider', cursor: 'pointer', flexShrink: 0,
                            }}>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3, wordBreak: 'break-word', flex: 1, mr: 1 }}>
                                        {ticket.title}
                                    </Typography>
                                    <Chip label={ticket.ticketStatus} size="small"
                                        color={ticket.ticketStatus === 'ACTIVE' ? 'success' : 'secondary'}
                                        sx={{ fontWeight: 600, flexShrink: 0 }} />
                                </Box>
                                <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                                    <Chip label={PRIORITY_LABELS[ticket.priority] || ticket.priority}
                                        size="small" color={PRIORITY_COLORS[ticket.priority] || 'default'} />
                                    <Chip label={ticket.productName} size="small" variant="outlined" />
                                </Box>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="body2" color="text.secondary" sx={{
                                    overflow: 'hidden', textOverflow: 'ellipsis',
                                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                                }}>
                                    {ticket.description}
                                </Typography>
                                <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
                                    Created: {formatDate(ticket.createdAt)}
                                </Typography>
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>

            {tickets.length === 0 && !loading && (
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
                    <Typography color="text.secondary">
                        {debouncedSearch ? 'No tickets match your search.' : 'No tickets found. Create one to get started.'}
                    </Typography>
                </Paper>
            )}

            {totalCount > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, pt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        Showing {tickets.length} of {totalCount} ticket{totalCount !== 1 ? 's' : ''}
                    </Typography>
                    {hasMoreTiles && (
                        <Button variant="outlined" color="secondary"
                            onClick={handleTileLoadMore}
                            disabled={loadingMore}>
                            {loadingMore ? <CircularProgress size={20} color="secondary" /> : 'Load More'}
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    );

    const renderTableView = () => (
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Last Updated</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tickets.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">
                                        {debouncedSearch ? 'No tickets match your search.' : 'No tickets found.'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : tickets.map((ticket) => (
                            <TableRow key={ticket._id} hover>
                                <TableCell sx={{ maxWidth: 250 }}>
                                    <Typography variant="body2" fontWeight={600} noWrap>{ticket.title}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip label={ticket.productName} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell>
                                    <Chip label={PRIORITY_LABELS[ticket.priority] || ticket.priority}
                                        size="small" color={PRIORITY_COLORS[ticket.priority] || 'default'} />
                                </TableCell>
                                <TableCell>
                                    <Chip label={ticket.ticketStatus} size="small"
                                        color={ticket.ticketStatus === 'ACTIVE' ? 'success' : 'secondary'} />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption">{formatDate(ticket.createdAt)}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption">{formatDate(ticket.updatedAt)}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                        <Tooltip title="View">
                                            <Button color="secondary" variant="contained" size="small"
                                                onClick={() => navigate(`/support/my-tickets/${ticket._id}`)}
                                                sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                                                <Visibility fontSize="small" />
                                            </Button>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {totalCount > 0 && (
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={tablePage}
                    onPageChange={handleTablePageChange}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />
            )}
        </Paper>
    );

    const toolbarBgSx = {
        backgroundColor: (t: any) =>
            t.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
    };

    return (
        <Box pl={{ xs: 1.25, md: 2.5 }} pt={1} width="100%" mb={3}>
            <DynamicBreadcrumbs
                items={[
                    { itemPath: '/dashboard', itemLabel: 'Home' },
                    { itemPath: '/support/my-tickets', itemLabel: 'My Tickets' },
                ]}
            />

            {/* Toolbar */}
            <Box
                sx={{
                    display: 'flex', flexDirection: 'row', gap: { xs: 1, md: 2 },
                    alignItems: 'center', mb: 3, p: 1.5, borderRadius: 2, flexWrap: { xs: 'wrap', md: 'nowrap' },
                    ...toolbarBgSx,
                }}>
                {/* Mobile: Search toggle */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
                    <IconButton color="secondary" onClick={() => setMobileSearchOpen(prev => !prev)} size="large"
                        sx={{ border: '1px solid', borderColor: 'secondary.main', '&:hover': { backgroundColor: 'action.hover', borderColor: 'secondary.dark' } }}>
                        <Search />
                    </IconButton>
                </Box>

                {/* Desktop: Search */}
                <Box sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1, maxWidth: 280 }}>
                    <TextField label="Search by Title" variant="outlined" color="secondary" size="small"
                        value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} fullWidth />
                </Box>

                <Box sx={{ display: { xs: 'flex', md: 'none' }, flexGrow: 1 }} />

                {/* Desktop: Filter + Sort + View Toggle + Create */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, ml: 'auto' }}>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel color="secondary">Filter By</InputLabel>
                        <Select value={filterBy} label="Filter By" onChange={(e) => setFilterBy(e.target.value)} color="secondary">
                            {FILTER_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel color="secondary">Sort By</InputLabel>
                        <Select value={sortBy} label="Sort By" onChange={(e) => setSortBy(e.target.value)} color="secondary">
                            {SORT_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 90 }}>
                        <InputLabel color="secondary">Order</InputLabel>
                        <Select value={sortOrder} label="Order" onChange={(e) => setSortOrder(e.target.value as 'ASC' | 'DESC')} color="secondary">
                            <MenuItem value="ASC">ASC</MenuItem>
                            <MenuItem value="DESC">DESC</MenuItem>
                        </Select>
                    </FormControl>

                    <ToggleButtonGroup value={viewMode} exclusive
                        onChange={(_e, newMode) => { if (newMode !== null) { setViewMode(newMode); localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode); } }}
                        size="small"
                        sx={{
                            '& .MuiToggleButton-root': {
                                border: '1px solid', borderColor: 'divider',
                                '&.Mui-selected': {
                                    backgroundColor: (t) => t.palette.toggle?.selectedBg ?? (t.palette.mode === 'dark' ? '#191f45' : t.palette.secondary.main),
                                    color: (t) => t.palette.toggle?.selectedColor ?? (t.palette.mode === 'dark' ? '#fff' : t.palette.secondary.contrastText),
                                    '&:hover': { backgroundColor: (t) => t.palette.toggle?.selectedHoverBg ?? (t.palette.mode === 'dark' ? '#232a54' : t.palette.secondary.dark) },
                                },
                            },
                        }}>
                        <ToggleButton value="tile" aria-label="Tile view">
                            <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
                        </ToggleButton>
                        <ToggleButton value="table" aria-label="Table view">
                            <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <Button variant="outlined" color="secondary" startIcon={<Add />} onClick={handleCreate}
                        size="small" sx={{ whiteSpace: 'nowrap' }}>
                        New Ticket
                    </Button>
                </Box>

                {/* Mobile: Create */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
                    <IconButton color="secondary" onClick={handleCreate} size="large"
                        sx={{ border: '1px solid', borderColor: 'secondary.main', '&:hover': { backgroundColor: 'action.hover', borderColor: 'secondary.dark' } }}>
                        <Add />
                    </IconButton>
                </Box>
            </Box>

            {/* Mobile: expanded search + sort */}
            {mobileSearchOpen && (
                <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5, mb: 2 }}>
                    <TextField label="Search by Title" variant="outlined" color="secondary" size="small"
                        fullWidth autoFocus value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                    <FormControl size="small" fullWidth>
                        <InputLabel color="secondary">Filter By</InputLabel>
                        <Select value={filterBy} label="Filter By" onChange={(e) => setFilterBy(e.target.value)} color="secondary">
                            {FILTER_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box display="flex" gap={1}>
                        <FormControl size="small" fullWidth>
                            <InputLabel color="secondary">Sort By</InputLabel>
                            <Select value={sortBy} label="Sort By" onChange={(e) => setSortBy(e.target.value)} color="secondary">
                                {SORT_OPTIONS.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 90 }}>
                            <InputLabel color="secondary">Order</InputLabel>
                            <Select value={sortOrder} label="Order" onChange={(e) => setSortOrder(e.target.value as 'ASC' | 'DESC')} color="secondary">
                                <MenuItem value="ASC">ASC</MenuItem>
                                <MenuItem value="DESC">DESC</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
            )}

            {loading && tickets.length === 0 ? (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress color="secondary" />
                </Box>
            ) : viewMode === 'tile' ? renderTileView() : renderTableView()}

            {dialogOpen && (
                <NewTicketDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onCreated={handleTicketCreated} />
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default MyTickets;
